import { useContext, useMemo } from 'react'

import { shallowEqual, useSelector } from 'react-redux'

import { EMPTY_OBJECT } from '@constants'
import { CampaignContext } from '@context/Campaign'
import { ApprovalType, selectIsPriceHidden, selectIsUnlockedCampaign } from '@store/campaigns'
import { IApplicationState } from '@store/index'
import {
  selectIsFetching,
  selectIsPurchasing,
  selectMediaObjectLicenseOptions,
} from '@store/licenses'
import {
  selectFirstErrorCode,
  selectHasInsufficientFundError,
  selectMediaObject,
} from '@store/mediaObjects'
import { selectIsInboxView } from '@store/router'
import {
  ISubmission,
  selectActiveSubmissionMediaObject,
  selectSubmission,
} from '@store/submissions'
import { selectUIFlag, UIFlag } from '@store/ui'

interface IUsePurchaseLicenseDataProps {
  approvalType?: ApprovalType
  submissionId?: number
  frameNumber?: number
  currentMediaObjectId?: number
}

interface IUsePurchaseLicenseDataReturn {
  mediaObject: any
  licenseOptions: readonly any[]
  isFetching: boolean
  isInboxView: boolean
  showPriceSummary: boolean
  shouldHideModal: boolean
  submission: ISubmission
  isUnlockedCampaign: boolean
  hasInsufficientFundError: boolean
  errorCode: string
  isRequesting: boolean
  campaignId: number
}

export const usePurchaseLicenseData = ({
  approvalType,
  frameNumber,
  submissionId,
  currentMediaObjectId,
}: IUsePurchaseLicenseDataProps): IUsePurchaseLicenseDataReturn => {
  const {
    campaign: { id: campaignId },
  } = useContext(CampaignContext)

  const data = useSelector((state: IApplicationState) => {
    let activeMediaObject

    if (currentMediaObjectId) {
      activeMediaObject = selectMediaObject(state, currentMediaObjectId)
    } else {
      activeMediaObject = selectActiveSubmissionMediaObject(state, submissionId, frameNumber)
    }

    return {
      mediaObject: activeMediaObject,
      // raw (stored-ref) options; filtered below in useMemo so the filtered array keeps a
      // stable reference — filtering inside useSelector returns a fresh array each render
      // and loops under react-redux 9.
      licenseOptions: selectMediaObjectLicenseOptions(state, activeMediaObject.id),
      submission: submissionId
        ? selectSubmission(state, submissionId)
        : (EMPTY_OBJECT as ISubmission),
      isFetching: selectIsFetching(state),
      hasInsufficientFundError: selectHasInsufficientFundError(state),
      errorCode: selectFirstErrorCode(state),
      isRequesting: selectIsPurchasing(state),
      isInboxView: selectIsInboxView(state),
      showPriceSummary: !selectIsPriceHidden(state, campaignId),
      shouldHideModal: Boolean(selectUIFlag(state, UIFlag.HIDE_MODAL)),
      isUnlockedCampaign: selectIsUnlockedCampaign(state, campaignId),
      campaignId,
    }
  }, shallowEqual)

  const licenseOptions = useMemo(
    () =>
      data.licenseOptions.filter((option) =>
        approvalType === 'pre-approval' ? option?.preapproval : !option?.preapproval,
      ),
    [data.licenseOptions, approvalType],
  )

  return { ...data, licenseOptions }
}
